import { useMemo } from 'react';

import { EmployerCreateDto, EmployerUpdateDto } from '@libs/dto';
import { ApiError, ApiResponse } from '@libs/types';
import { useEmployerCreateMutation, useEmployerUpdateMutation, useEmployerDeleteMutation } from '@carcareers/store';
import { useLoading, useSnackbar, useConfirmation } from '@carcareers/contexts';
import { isError, getErrorsAsString, getErrors } from '@carcareers/utils';
import { Employer } from '@libs/models';

interface EmployerCallbacks {
  onSuccess?: () => void;
}

export const useEmployer = () => {
  const { setLoading, setLabel } = useLoading();
  const { toggleSnackbar } = useSnackbar();
  const { confirm } = useConfirmation();
  const [employerCreate] = useEmployerCreateMutation();
  const [employerUpdate] = useEmployerUpdateMutation();
  const [employerDelete] = useEmployerDeleteMutation();

  return useMemo(
    () => ({
      createEmployer: async (
        employer?: EmployerCreateDto,
        callbacks?: EmployerCallbacks,
      ): Promise<ApiResponse<Employer>> => {
        if (!employer) {
          toggleSnackbar('warning', 'Employer data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Creating employer...');
        setLoading(true);
        const response = await employerCreate(employer);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Employer created');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      updateEmployer: async (
        employer?: EmployerUpdateDto,
        callbacks?: EmployerCallbacks,
      ): Promise<ApiResponse<Employer>> => {
        if (!employer) {
          toggleSnackbar('warning', 'Employer data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Updating employer...');
        setLoading(true);
        const response = await employerUpdate(employer);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Employer updated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      deleteEmployer: async (id?: number, callbacks?: EmployerCallbacks) => {
        if (!id) return;

        const confirmed = await confirm({
          title: 'Delete Employer',
          message: 'Are you sure you want to delete this employer?',
        });

        if (!confirmed) return;

        setLabel('Deleting employer...');
        setLoading(true);
        const response = await employerDelete(id);

        if (isError(response)) {
          toggleSnackbar('error', getErrorsAsString(response));
        } else {
          toggleSnackbar('success', 'Employer deleted');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        }

        setLoading(false);

        return response;
      },
    }),
    [toggleSnackbar, confirm, setLoading, setLabel, employerCreate, employerUpdate, employerDelete],
  );
};
